/*
===============================================================
  ____    ___    ____   _   __       __                
 |  _ |  / _ \  |  _ \ | | |   \    |   \  ___  __ __  
 |  __| | |_| | |  __/ | | | |) | o | |) |/ -_) \ V /  
 |_|\_\ |_| |_| |_|    |_| |___/    |___/ \___|  \_/    o o o   

===============================================================
*/

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

// @mui material components
import Icon from "@mui/material/Icon";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import noavatar from "assets/images/noavatar.jpg";
import { useTranslation } from "react-i18next";
import { APP_STATE, API } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import ProfileInfo from "./ProfileInfo";
import UploadAvatar from "./UploadAvatar";

const initAttr = {
  id: "",
  name: "",
  email: "",
  phone_no: "",
  avatar: "",
  password: "",
  language: "th",
  created_at: "",
  updated_at: "",
};

function Profile() {
  const mod = "profile";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const init = () => {
    FetchData("GET", `/app/req/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "OK") {
        setAttr(result.data);
      } else {
        Func.exitApp();
      }
    });
  };

  const edit = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData("PATCH", `/app/req/${mod}`, app.token, {
          name: attr.name,
          phone_no: attr.phone_no,
        }).then((result) => {
          setLoading(false);

          if (result.status === "OK") {
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const upload = (filedata) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        filedata = filedata.split("data:image/jpeg;base64,").join("");

        FetchData("POST", `/app/req/${mod}/avatar_upload`, app.token, {
          file: filedata,
        }).then((result) => {
          init();
          setLoading(false);

          if (result.status === "OK") {
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderEdit = () => {
    return (
      <MDBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Card
              sx={{
                "&:hover .card-header": {
                  transform: "translate3d(0, -50px, 0)",
                },
              }}
            >
              <MDBox
                position="relative"
                borderRadius="lg"
                mt={3}
                mx={3}
                className="card-header"
                sx={{ transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)" }}
              >
                <MDBox
                  component="img"
                  src={attr.avatar === null ? noavatar : API + attr.avatar}
                  alt="Product Image"
                  borderRadius="lg"
                  shadow="sm"
                  width="100%"
                  height="100%"
                  position="relative"
                  zIndex={10}
                  mb={2}
                />
              </MDBox>
              <MDBox textAlign="center" pt={2} px={3}>
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mt={-10}
                  position="relative"
                  zIndex={1}
                >
                  <MDBox mr={1}>
                    <UploadAvatar uploadData={upload} />
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Card>
              <ProfileInfo attr={attr} setAttr={setAttr} />
              <MDBox display="flex" justifyContent="flex-end" mr={3} mb={3}>
                <MDButton variant="gradient" color="info" onClick={edit}>
                  save
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.user`), t(`menu.profile`)]} />
      {renderEdit()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Profile;
